import axios from 'axios';
import {
  isServerUnavailableError,
  markServerAvailable,
  markServerUnavailable,
  shouldTrackConnectivity,
} from '../services/appRuntime';
import { clearSession, forceLogout, isLogoutInProgress } from '../services/sessionAuth';
import {
  startSignedProxyCacheRenewal,
  warmSignedProxyCacheFromPayload,
} from '../utils/signedImageProxyCache';
import { noteServerAppVersion } from '../utils/appVersionSync';
import { attachTraceHeaders, reportSessionEvent } from '../utils/sessionTrace';

let isRefreshing = false;
let refreshQueue = [];

const AUTH_EXEMPT_PATHS = [
  '/auth/refresh',
  '/auth/signin',
  '/auth/signout',
];

function getRequestUrl(config) {
  const raw = config?.url ?? '';
  if (raw.startsWith('http')) {
    try {
      return new URL(raw).pathname;
    } catch {
      return raw;
    }
  }
  return raw;
}

function isAuthExemptRequest(config) {
  const url = getRequestUrl(config);
  return AUTH_EXEMPT_PATHS.some((path) => url.includes(path));
}

function processQueue(error, token = null) {
  refreshQueue.forEach((promise) => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });
  refreshQueue = [];
}

function handleRefreshFailure(refreshError) {
  processQueue(refreshError, null);

  const status = refreshError?.response?.status;
  if (status === 401 || status === 403) {
    forceLogout({ reason: 'expired' });
    return;
  }

  if (isServerUnavailableError(refreshError)) {
    markServerUnavailable(refreshError);
    return;
  }

  forceLogout({ reason: 'invalid' });
}

export function setupHttpInterceptors() {
  if (axios.__cddInterceptorsInstalled) {
    return;
  }
  axios.__cddInterceptorsInstalled = true;
  startSignedProxyCacheRenewal();

  axios.interceptors.request.use((config) => {
    if (!config.skipSessionTrace) {
      config.headers = attachTraceHeaders(config.headers || {});
    }
    return config;
  });

  axios.interceptors.response.use(
    async (response) => {
      if (shouldTrackConnectivity(response.config)) {
        markServerAvailable();
      }

      const url = getRequestUrl(response.config);
      if (!url.includes('/image-proxy') && !url.includes('/logger')) {
        noteServerAppVersion(response.headers?.['x-app-version']);
      }
      const isPublicQr = url.includes('findOneForQrCodeReview');
      if (!isPublicQr && localStorage.getItem('token') && response?.data) {
        // Do not block the UI. Authenticated API now attaches signed display_urls;
        // this warm is a leftover-key fallback and must not delay the fiche.
        void warmSignedProxyCacheFromPayload(response.data);
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config ?? {};
      const status = error.response?.status;

      if (shouldTrackConnectivity(originalRequest) && isServerUnavailableError(error)) {
        markServerUnavailable(error);
      }

      if (status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh) {
        if (isAuthExemptRequest(originalRequest)) {
          if (getRequestUrl(originalRequest).includes('/auth/refresh')) {
            handleRefreshFailure(error);
          }
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const response = await axios.post(
              '/auth/refresh',
              {},
              { withCredentials: true, skipAuthRefresh: true },
            );

            if (response?.status === 200 || response?.status === 201) {
              const newAccessToken = response.data;
              localStorage.setItem('token', newAccessToken);
              window.dispatchEvent(new Event('storage'));
              axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
              processQueue(null, newAccessToken);
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return axios(originalRequest);
            }

            const unexpectedRefresh = new Error('Token refresh returned an unexpected response');
            handleRefreshFailure(unexpectedRefresh);
            return Promise.reject(unexpectedRefresh);
          } catch (refreshError) {
            handleRefreshFailure(refreshError);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axios(originalRequest));
            },
            reject,
          });
        });
      }

      if (status === 401 && !isLogoutInProgress() && !isAuthExemptRequest(originalRequest)) {
        forceLogout({ reason: 'expired' });
      }

      const url = getRequestUrl(originalRequest);
      if (
        !originalRequest.skipSessionTrace
        && status >= 500
        && !url.includes('/logger')
        && !url.includes('/image-proxy')
      ) {
        void reportSessionEvent('ui.api.error', {
          level: 'error',
          path: url,
          status,
          message: error.message,
        });
      }

      return Promise.reject(error);
    },
  );
}

// Backwards-compatible helper used in a few screens.
export function clearLocalStorageKeys() {
  clearSession();
}
